import { Component, computed, inject, input, output, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { AccordionComponent, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { TextModel } from '../../../models/primitive/text.model';
import { safeStringify } from '@evorait/pace-components';
import { TranslateModule } from '@ngx-translate/core';
import { AddressState } from '../../../state/address/address-collection.state';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEyeSlash, faServer, faTrashCan, faUserPen } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import {
    deleteAddressRequested,
    hideAddressRequested,
    restoreAddressRequested,
    unhideAddressRequested,
} from '../../../state/address';

@Component({
    selector: 'pace-core-address-spector-ngrx',
    imports: [
        CommonModule,
        AlertComponent,
        AccordionComponent,
        AccordionPanelComponent,
        TranslateModule,
        FaIconComponent,
    ],
    templateUrl: './address-spector-ngrx.component.html',
    styleUrl: './address-spector-ngrx.component.css',
})
export class AddressSpectorNgrxComponent {
    public addresses = input<AddressState[] | undefined | null>([]);
    public items: Signal<
        Array<{
            id: string;
            heading: string;
            summary: string[];
            details: { [p: string]: string };
            panelClass: string;
            deleted: boolean;
            visible: boolean;
            manual: boolean;
        }>
    > = computed(() => {
        const addresses = this.addresses();
        if (!addresses?.length) {
            return [];
        }
        return addresses.map((addr, index) => ({
            id: addr.id,
            heading: this.headingFor(addr, index),
            summary: this.summary(addr),
            details: this.details(addr),
            panelClass: this.panelClass(addr),
            deleted: addr.deleted,
            visible: addr.visible,
            manual: !addr.isFromAddressSet,
        }));
    });

    protected readonly faTrashCan = faTrashCan;
    protected readonly faEyeSlash = faEyeSlash;
    protected readonly faUserPen = faUserPen;
    protected readonly faServer = faServer;

    private readonly store = inject(Store);

    public downloadJson = output<AddressState[]>();

    protected readonly preferredOrder: Array<keyof AddressState> = [
        'name1',
        'name2',
        'name3',
        'name4',
        'name1shortP58',
        'name2shortP58',
        'street',
        'postCode',
        'city',
        'state',
        'country',
        'stateOfUSA',
        'building',
        'room',
        'floor',
        'department',
        'contactName1',
        'contactName2',
        'contactType',
        'email',
        'phone',
        'contactPhone',
        'fax1',
        'fax2',
        'fax3',
        'fax4',
        'addrText',
        'addrNumber',
        'visible',
        'isFromAddressSet',
        'deleted',
    ];

    public headingFor(addr: AddressState, index: number): string {
        const name = this.compactJoin([addr?.name1, addr?.name2]);
        if (name) {
            return this.compactJoin([name, addr?.country]);
        }

        return `Address #${index + 1}`;
    }

    public compactJoin(parts: Array<string | undefined | null>, sep = ', '): string {
        return parts
            .filter(Boolean)
            .map((p) => String(p).trim())
            .filter(Boolean)
            .join(sep);
    }

    private summary(addr: AddressState): string[] {
        if (!addr) {
            return [];
        }
        const lines: string[] = [];

        const name = this.compactJoin([addr.name1, addr.name2, addr.name3, addr.name4], ' / ');
        if (name) {
            lines.push(name);
        }

        const street = addr?.street;
        if (street) {
            lines.push(street);
        }

        const cityLine = this.compactJoin([addr.postCode, addr.city, addr.state], ' ');
        if (cityLine) {
            lines.push(cityLine);
        }

        const country = addr?.country;
        if (country) {
            lines.push(country);
        }

        return lines;
    }

    private flag(label: string, v: unknown): string {
        return v === true ? `${label}: ✅` : v === false ? `${label}: ❌` : '';
    }

    private details(addr: AddressState): { [k: string]: string } {
        const rawEntries = Object.entries(addr ?? {});
        const rawKeys = new Set(rawEntries.map(([k]) => k));

        const details: { [k: string]: string } = {};
        for (const k of this.preferredOrder) {
            if (rawKeys.has(k)) {
                details[k] = this.prettyValue(addr[k]);
            }
        }
        return details;
    }

    private panelClass(addr: AddressState): string {
        const classes: string[] = [];
        if (addr.deleted) {
            classes.push('address-spector-deleted');
        }
        if (!addr.visible) {
            classes.push('address-spector-invisible');
        }
        if (!addr.addrKey) {
            classes.push('address-spector-manual');
        }
        return classes.join(' ');
    }

    private prettyValue(v: boolean | TextModel | string | number | unknown): string {
        if (v === null || v === undefined) {
            return '';
        }
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            return String(v);
        }

        if (v instanceof TextModel) {
            return v.value;
        }

        return safeStringify(v);
    }

    public copyJson(): void {
        const raw = this.addresses();
        if (!raw) {
            return;
        }
        const text = JSON.stringify(raw, null, 2);
        navigator.clipboard?.writeText(text).catch(console.error);
    }

    public triggerDownloadJson(): void {
        const model = this.addresses();
        if (!model) {
            return;
        }
        this.downloadJson.emit(model);
    }

    protected unhide(id: string): void {
        this.store.dispatch(unhideAddressRequested({ id, correlationId: 'irrelevant' }));
    }

    protected hide(id: string): void {
        this.store.dispatch(hideAddressRequested({ id, correlationId: 'irrelevant' }));
    }

    protected undelete(id: string): void {
        this.store.dispatch(restoreAddressRequested({ id, correlationId: 'irrelevant' }));
    }

    protected delete(id: string): void {
        this.store.dispatch(deleteAddressRequested({ id, correlationId: 'irrelevant' }));
    }
}
